import { FC } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import { Chip, Cluster, DocumentIcon, FeedItem, useToaster } from '@forto-dev/phoenix-ui-core'

import { documentTypesQueryOptions, getEntityDocument } from '@/features/documents'
import { useLocalisation } from '@/utils/localisation'
import { localizeDocumentTypeLabel } from '@/utils/localisation/documentTypes'

import { ActivityFeedItemProps } from './types'

export const ActivityFeedItemDocumentUpload: FC<ActivityFeedItemProps> = ({ item, status }) => {
  const { translate, translateDate } = useLocalisation()
  const { data: documentTypes } = useSuspenseQuery(documentTypesQueryOptions())
  const toaster = useToaster()

  if (!item.activity.customField.feedV1Activity) {
    return null
  }

  const {
    _id: entityId,
    action,
    role,
    document_id: documentId,
    name: documentName,
  } = item.activity.customField.feedV1Activity
  const documentType =
    localizeDocumentTypeLabel(
      translate,
      role,
      role ? documentTypes.find((type) => type.code === role)?.displayName : undefined
    ) || '–'

  const handleDownload = async () => {
    if (documentId && entityId && documentName) {
      try {
        const { blob } = await getEntityDocument(entityId, documentId, documentName)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.download = documentName
        link.href = url
        link.setAttribute('data-document-id', documentId)
        document.body.appendChild(link)
        link.click()
        URL.revokeObjectURL(url)
        link.remove()
      } catch (error) {
        toaster({
          title: 'Error Downloading Document',
          description: error instanceof Error ? error.message : 'An error occurred, Please try later',
          variant: 'negative',
        })
      }
    }
  }

  return (
    <FeedItem
      type="message"
      name={item.activity.actor.fullName || 'Service'}
      action={`${translate(`feed.list.documentUpload.action.${action}`)} ${documentType}`}
      date={translateDate('feed_date', item.activity.createdAt)}
      suffix={item.activity.actor.type === 'Service' ? translate('feed.suffix.automated') : undefined}
      status={status}
    >
      <a id={`feed-activity-${item.id}`} />
      <Chip variant="neutral" onClick={handleDownload}>
        <Cluster gap={8} y="center">
          <DocumentIcon size="sm" />
          {documentName}
        </Cluster>
      </Chip>
    </FeedItem>
  )
}
